import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { authApi, type LoginDto } from '../api/auth'
import { useAuthStore } from '../store/auth'
import { UserCheck } from 'lucide-react'
import toast from 'react-hot-toast'

export function LoginPage() {
  useEffect(() => { document.title = 'Login' }, [])
  const navigate = useNavigate()
  const setAuth = useAuthStore(s => s.setAuth)
  const { register, handleSubmit, formState: { errors } } = useForm<LoginDto>()

  const mutation = useMutation({
    mutationFn: (dto: LoginDto) => authApi.login(dto).then(r => r.data),
    onSuccess: (data) => {
      setAuth(data.user, data.accessToken)
      if (data.user.role === 'administrator') {
        navigate('/admin')
      } else if (data.user.companyId) {
        navigate(`/company/${data.user.companyId}/leads`)
      } else {
        navigate('/')
      }
    },
    onError: () => toast.error('Invalid email or password'),
  })

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary-600 mb-4">
            <UserCheck className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-semibold text-gray-900">Welcome back</h1>
          <p className="text-sm text-gray-500 mt-1">Sign in to your CRM</p>
        </div>

        <form onSubmit={handleSubmit(d => mutation.mutate(d))} className="card p-6 space-y-4">
          <div>
            <label className="label">Email</label>
            <input
              {...register('email', { required: 'Email is required' })}
              type="email"
              autoComplete="email"
              className="input"
              placeholder="you@company.com"
            />
            {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email.message}</p>}
          </div>
          <div>
            <label className="label">Password</label>
            <input
              {...register('password', { required: 'Password is required' })}
              type="password"
              autoComplete="current-password"
              className="input"
              placeholder="••••••••"
            />
            {errors.password && <p className="text-xs text-red-600 mt-1">{errors.password.message}</p>}
          </div>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="btn-primary btn w-full"
          >
            {mutation.isPending ? 'Signing in...' : 'Sign in'}
          </button>
          <div className="text-center text-sm">
            <Link to="/forgot-password" className="text-primary-600 hover:underline">
              Forgot your password?
            </Link>
          </div>
        </form>
      </div>
    </div>
  )
}
